
import { Component, Prop, Vue, Mixins } from 'vue-property-decorator';
import { Getter, Action } from 'vuex-class';
import { Bar } from 'vue-chartjs';
import { ChartConfig } from './chartConfig';
import {Data, Series, SeriesConfig, SeriesOrderConfiguration} from '../../models';
import ColourService from '@/shared/services/color';
import {ChartData} from 'chart.js';
import { ChartDataFormat } from '@/features/reports/models/chartDataFormat';

@Component
export default class PercentageBarChart extends Mixins(Bar) {
	@Prop() public data: Data;
	@Prop() public reportId: string;
	@Prop() public seriesOrder: SeriesOrderConfiguration;
	@Prop() public seriesToCompareTo: string;
	public renderSeriesInReverse: boolean = true;

	public mounted() {
		const data = this.mapData(this.data);
		const options = this.overrideOptions(ChartConfig.gridChartsGlobalOptions);
		this.renderChart(data, options);
	}

	public overrideOptions(options: Chart.ChartOptions): Chart.ChartOptions {
		options.plugins!.inlineLabels = {
			reportId: this.reportId,
			reverseLabels: this.renderSeriesInReverse,
			dataFormat: ChartDataFormat.PERCENTAGE,
			defaultLabelValues: this.seriesToCompareTo ? this.calculatePercentageLabels() : undefined
		};
		options.plugins!.crosshair = false;
		options.scales = {
			xAxes: [{
				scaleLabel: {
					labelString: this.data.scale.title
				},
				ticks: {
					maxTicksLimit: 32
				},
				gridLines: {
					display: false
				}
			}],
			yAxes: [{
				ticks: {
					beginAtZero: true,
					max: 100,
					callback: (value: any) => value % 1 === 0 ? value + '%' : undefined
				}
			}]
		};

		options.tooltips!.callbacks = {
			label: (tooltipItem: Chart.ChartTooltipItem, data: ChartData) => {
				const index = tooltipItem.index!;
				const datasetIndex = tooltipItem.datasetIndex!;

				const value = data.datasets![datasetIndex].data![index];
				tooltipItem.value = `${value}%`;

				return tooltipItem.value;
			}
		};
		options.legend!.reverse = this.renderSeriesInReverse;
		return options;
	}

	public mapData(data: Data) {
		const backgroundPalette = ChartConfig.borderPalette(this.seriesRenderOrder);
		const borderPalette = ChartConfig.borderPalette(this.seriesRenderOrder);

		return {
			labels: data.scale.labels,
			datasets: this.orderedSeries.map((series, index) => ({
				label: this.getSeriesConfig(series.name).displayName,
				data: this.getPercentageValues(series),
				backgroundColor: backgroundPalette[index],
				borderColor: borderPalette[index],
				hoverBackgroundColor: ColourService.setAlpha(backgroundPalette[index], 0.4),
				borderWidth: 1
			}))
		};
	}

	private calculatePercentageLabels(): Map<string, string> {
		const labels = new Map<string, string>();

		const compareToSeries = this.getSeries(this.seriesToCompareTo);
		const compareToTotal = compareToSeries.data.reduce((sum, value) => sum + value, 0);

		this.data.series.forEach((series) => {
			const total = series.data.reduce((sum, value) => sum + value, 0);
			const percentageOfTotal = compareToTotal === 0 ? 0 : (total / compareToTotal) * 100;
			const roundedPercentage = Math.round(percentageOfTotal * 100) / 100;
			const displayName = this.getSeriesConfig(series.name)?.displayName;
			labels.set(displayName ? displayName : series.name, `${roundedPercentage.toString()}%`);
		});

		return labels;
	}

	private getPercentageValues(seriesData: Series): number[] {
		return seriesData.data.map((dataPoint, index) => {
			const totalValue = this.getSeries(this.seriesToCompareTo).data[index];
			const percentageOfTotal = totalValue === 0 ? 0 : (dataPoint / totalValue) * 100;
			return Math.round(percentageOfTotal * 100) / 100;
		});
	}

	private get orderedSeries(): Series[] {
		return this.seriesRenderOrder.map((seriesConfig => this.getSeries(seriesConfig.name)));
	}

	private getSeries(name: string): Series {
		return this.data.series.filter(series => series.name === name)[0];
	}

	private getSeriesConfig(name: string): SeriesConfig {
		return this.seriesConfig.filter(config => config.name === name)[0];
	}

	private get seriesConfig(): SeriesConfig[] {
		if (typeof this.seriesOrder[0] === 'string') {
			return (this.seriesOrder as string[]).map(seriesName => ({ name: seriesName, displayName: seriesName }));
		}
		return this.seriesOrder as SeriesConfig[];
	}

	private get seriesRenderOrder(): SeriesConfig[] {
		return this.renderSeriesInReverse ? this.seriesConfig.slice().reverse() : this.seriesConfig;
	}
}
